// Headless creator of a pending groupAccessRequests document for a freshly
// signed-up user. Called once from the post-onboarding handoff in
// app/_layout.web.tsx when a persisted signup param specified a groupId. The
// write shape is the same one components/RequestGroupAccess.web.tsx produces
// when an existing user clicks "Request access"; once written, the request
// surfaces through the existing notification pipeline for the group admin.

import type { Group } from "@/types/group";
import type { User } from "firebase/auth";
import {
  addDoc,
  collection,
  doc,
  Firestore,
  getDoc,
  getDocs,
  limit,
  query,
  serverTimestamp,
  where,
} from "firebase/firestore";

export type CreateGroupJoinRequestResult =
  | "created"
  | "already-member"
  | "already-pending"
  | "group-not-found";

export interface CreateGroupJoinRequestArgs {
  firestore: Firestore;
  user: User;
  groupId: string;
}

export const createGroupJoinRequest = async ({
  firestore,
  user,
  groupId,
}: CreateGroupJoinRequestArgs): Promise<CreateGroupJoinRequestResult> => {
  // 1. Group must exist. A bad groupId from a stale or malformed URL must
  // not produce an orphan request — the admin would see a notification for
  // a group that no longer exists.
  const groupSnap = await getDoc(doc(firestore, "groups", groupId));
  if (!groupSnap.exists()) {
    return "group-not-found";
  }
  const group = groupSnap.data() as Group;

  // 2. Already-a-member check. memberIds on the group doc is the same source
  // of truth that useGroupAccess.web.ts reads — using it avoids a second
  // round-trip to fetch the user doc's groupIds field.
  if (Array.isArray(group.memberIds) && group.memberIds.includes(user.uid)) {
    return "already-member";
  }

  // 3. Pending-request check. Same query shape as useGroupAccess.web.ts
  // lines 64-74; limit(1) because we only need to know whether one exists.
  const pendingSnap = await getDocs(
    query(
      collection(firestore, "groupAccessRequests"),
      where("requesterId", "==", user.uid),
      where("groupId", "==", groupId),
      where("status", "==", "pending"),
      limit(1),
    ),
  );
  if (!pendingSnap.empty) {
    return "already-pending";
  }

  // Same payload shape as components/RequestGroupAccess.web.tsx so the
  // admin notification pipeline treats it identically to a manually
  // submitted request. Phone-only users have no email and no displayName,
  // so we fall the display name back to the phone number — that keeps the
  // existing admin UI fallback chain
  // (`requesterDisplayName || requesterEmail || "USER"`) showing a useful
  // identifier instead of "USER". `requesterPhoneNumber` is also stored
  // explicitly for any future admin UI that wants to surface it as its
  // own field.
  await addDoc(collection(firestore, "groupAccessRequests"), {
    groupId,
    groupName: group.name,
    requesterId: user.uid,
    requesterEmail: user.email ?? "",
    requesterDisplayName: user.displayName || user.phoneNumber || "",
    requesterPhoneNumber: user.phoneNumber ?? "",
    requesterPhotoURL: user.photoURL ?? "",
    status: "pending",
    createdAt: serverTimestamp(),
  });

  return "created";
};
